/**
 * UPT-2079 L4 (web §5.10, rest §4.6) — génération DÉTERMINISTE de lignes d'exemple, miroir TypeScript de
 * `PreviewRowGenerator.java` (même algorithme, sans variante, vérifié par `preview-generator-vectors.json` lu tel quel
 * par jest et par JUnit). Aucun `Math.random`, aucune horloge (`Date.now`, `new Date()` sans argument), aucune locale
 * (`toLocale*`, `Intl`) : même entrée, même sortie.
 *
 * - `fnv1a32(texte)` : FNV-1a 32 bits sur les octets UTF-8, départ `PREVIEW_GENERATOR.hashOffset`, pour chaque octet
 *   `h ^= octet ; h = (h × hashPrime) mod 2^32`, entier non signé.
 * - Pour la ligne de rang `i` (à partir de 0, `n = i + 1`) et le champ `f` : `h = fnv1a32(entityCode|f.name|i|seed)`.
 * - Pas de donnée personnelle : dictionnaires génériques (« Libellé 3 », `contact3@example.org`, `https://example.org/…`).
 */
import { cutUtf16 } from './previewSnapshot';
import {
  PREVIEW_GENERATOR,
  PREVIEW_LIMITS,
  type PreviewRow,
  type PreviewSnapshotField,
  type PreviewValue,
} from '../data/contracts';

const DAY_MS = 86_400_000;
const NUMBER_DEFAULT_LOW = 1;
const NUMBER_DEFAULT_HIGH = 100;
const NUMBER_MAX_SPAN = 999_999;
const AMOUNT_MODULO = 1_000_000;
const AMOUNT_DIVISOR = 100;
const SEPARATOR = '|';

/** FNV-1a 32 bits sur les octets UTF-8 (`Math.imul`, entier non signé). */
export const fnv1a32 = (text: string): number => {
  const bytes = new TextEncoder().encode(text);
  let hash: number = PREVIEW_GENERATOR.hashOffset;
  for (let index = 0; index < bytes.length; index += 1) {
    hash ^= bytes[index];
    hash = Math.imul(hash, PREVIEW_GENERATOR.hashPrime) >>> 0;
  }
  return hash >>> 0;
};

/** Valeur numérique d'un validateur : un nombre ou une chaîne numérique ; sinon `undefined`. */
const numeric = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const validatorValue = (field: PreviewSnapshotField, type: string): number | undefined => {
  const validator = (field.validators ?? []).find((entry) => entry && entry.type === type);
  return validator ? numeric(validator.value) : undefined;
};

/** `label.fr` du champ si c'est une chaîne non vide, sinon son nom. */
const labelOf = (field: PreviewSnapshotField): string => {
  const label = field.label;
  if (label && typeof label === 'object' && typeof label.fr === 'string' && label.fr.length > 0) {
    return label.fr;
  }
  return field.name;
};

const pad2 = (value: number): string => String(value).padStart(2, '0');

const timeOf = (hash: number): string => `${pad2((hash >>> 9) % 24)}:${pad2(((hash >>> 17) % 4) * 15)}`;

const dateOf = (hash: number): string => {
  const base = Date.parse(`${PREVIEW_GENERATOR.baseDate}T00:00:00Z`);
  return new Date(base + (hash % PREVIEW_GENERATOR.dateSpanDays) * DAY_MS).toISOString().slice(0, 10);
};

const staticOptions = (field: PreviewSnapshotField): { value: string }[] =>
  field.data && field.data.type === 'static' && Array.isArray(field.data.options) ? field.data.options : [];

const textOf = (field: PreviewSnapshotField, n: number): string => {
  let text = `${labelOf(field)} ${n}`;
  const minLength = validatorValue(field, 'minLength');
  if (minLength !== undefined && minLength >= 1 && text.length < minLength) {
    text = text.padEnd(Math.floor(minLength), PREVIEW_GENERATOR.textPad);
  }
  const maxLength = validatorValue(field, 'maxLength');
  if (maxLength !== undefined && maxLength >= 1 && text.length > maxLength) {
    text = cutUtf16(text, Math.floor(maxLength));
  }
  return cutUtf16(text, PREVIEW_LIMITS.valueMaxChars);
};

const numberOf = (field: PreviewSnapshotField, hash: number): number => {
  const minValue = validatorValue(field, 'minValue');
  const maxValue = validatorValue(field, 'maxValue');
  const low = minValue === undefined ? NUMBER_DEFAULT_LOW : Math.ceil(minValue);
  let high = maxValue === undefined ? NUMBER_DEFAULT_HIGH : Math.floor(maxValue);
  high = Math.max(high, low);
  high = Math.min(high, low + NUMBER_MAX_SPAN);
  return low + (hash % (high - low + 1));
};

const amountOf = (field: PreviewSnapshotField, hash: number): number => {
  let amount = (hash % AMOUNT_MODULO) / AMOUNT_DIVISOR;
  const minValue = validatorValue(field, 'minValue');
  const maxValue = validatorValue(field, 'maxValue');
  if (minValue !== undefined && amount < minValue) amount = minValue;
  if (maxValue !== undefined && amount > maxValue) amount = maxValue;
  return amount;
};

/** Valeur d'un champ pour la ligne `n` (à partir de 1) ; `undefined` = clé absente (types sans valeur). */
const valueOf = (field: PreviewSnapshotField, hash: number, n: number): PreviewValue | undefined => {
  switch (field.type) {
    case 'hidden':
    case 'attachments':
    case 'references':
    case 'entityref':
      return undefined;
    case 'number':
      return numberOf(field, hash);
    case 'amount':
      return amountOf(field, hash);
    case 'date':
      return dateOf(hash);
    case 'time':
      return timeOf(hash);
    case 'datetime':
      return `${dateOf(hash)}T${timeOf(hash)}`;
    case 'checkbox':
      return hash % 2 === 1;
    case 'select':
    case 'radio': {
      const options = staticOptions(field);
      return options.length > 0 ? options[hash % options.length].value : null;
    }
    case 'typeahead': {
      const options = staticOptions(field);
      return options.length > 0 ? options[hash % options.length].value : textOf(field, n);
    }
    case 'email':
      return `contact${n}@example.org`;
    case 'color':
      return PREVIEW_GENERATOR.palette[hash % PREVIEW_GENERATOR.palette.length];
    case 'link':
      return `https://example.org/${field.name}/${n}`;
    default:
      // text, textarea, display (et, par repli, un type hors de l'énumération du schéma : jamais servi par le serveur)
      return textOf(field, n);
  }
};

/** `count` lignes d'exemple de l'entité (`_id` = `r<n>`, puis les champs dans l'ordre de l'instantané). */
export const generateRows = (
  entityCode: string,
  fields: readonly PreviewSnapshotField[],
  count: number,
  seed: number
): PreviewRow[] => {
  const rows: PreviewRow[] = [];
  for (let index = 0; index < count; index += 1) {
    const row: PreviewRow = { _id: `r${index + 1}` };
    for (const field of fields) {
      const hash = fnv1a32([entityCode, field.name, String(index), String(seed)].join(SEPARATOR));
      const value = valueOf(field, hash, index + 1);
      if (value !== undefined) {
        row[field.name] = value;
      }
    }
    rows.push(row);
  }
  return rows;
};
